import { OffsetMap } from '../types/offsetMap'

/**
 * Result of frame path finding
 */
export interface FramePath {
  path: number[]                    // Array of frame indices from start to end
  totalOffset: { x: number; y: number } // Cumulative dx/dy along the path
  valid: boolean                    // Whether a valid path was found
}

/**
 * Find the shortest path between two frames using BFS on the connectivity graph.
 * Returns the path as an array of frame indices and the cumulative offset.
 *
 * @param startFrame - Starting frame index
 * @param endFrame - Target frame index
 * @param connectivityGraph - Map of frame connections
 * @param offsetMap - Offset map for calculating displacements
 * @returns FramePath with path, cumulative offset, and validity
 */
export function findFramePath(
  startFrame: number,
  endFrame: number,
  connectivityGraph: Map<number, Set<number>> | null,
  offsetMap: OffsetMap | null
): FramePath {
  // If same frame, return trivial path
  if (startFrame === endFrame) {
    return {
      path: [startFrame],
      totalOffset: { x: 0, y: 0 },
      valid: true
    }
  }

  // If no connectivity graph, can't find path
  if (!connectivityGraph) {
    console.warn(`[findFramePath] No connectivity graph available for ${startFrame}→${endFrame}`)
    return {
      path: [],
      totalOffset: { x: 0, y: 0 },
      valid: false
    }
  }

  // BFS to find shortest path
  const visited = new Set<number>()
  const queue: Array<{ frame: number; path: number[] }> = [{ frame: startFrame, path: [startFrame] }]
  const parent = new Map<number, number>()

  visited.add(startFrame)

  while (queue.length > 0) {
    const { frame, path } = queue.shift()!

    // Found target
    if (frame === endFrame) {
      // Calculate cumulative offset along the path
      const totalOffset = calculatePathOffset(path, offsetMap)
      // console.log(`[findFramePath] Found path ${startFrame}→${endFrame}: ${path.join('→')} (offset: ${totalOffset.x.toFixed(1)}, ${totalOffset.y.toFixed(1)})`)
      return {
        path,
        totalOffset,
        valid: true
      }
    }

    // Explore neighbors
    const neighbors = connectivityGraph.get(frame)
    if (neighbors) {
      for (const neighbor of neighbors) {
        if (!visited.has(neighbor)) {
          visited.add(neighbor)
          parent.set(neighbor, frame)
          queue.push({
            frame: neighbor,
            path: [...path, neighbor]
          })
        }
      }
    }
  }

  // No path found
  console.warn(`[findFramePath] No path found from ${startFrame} to ${endFrame}`)
  return {
    path: [],
    totalOffset: { x: 0, y: 0 },
    valid: false
  }
}

/**
 * Calculate cumulative offset along a path of frames.
 * Sums dx/dy between consecutive frames in the path.
 *
 * @param path - Array of frame indices representing the path
 * @param offsetMap - Offset map containing frame displacements
 * @returns Cumulative {x, y} offset along the path
 */
export function calculatePathOffset(
  path: number[],
  offsetMap: OffsetMap | null
): { x: number; y: number } {
  if (!offsetMap || path.length < 2) {
    return { x: 0, y: 0 }
  }

  let totalX = 0
  let totalY = 0
  // console.log(`[calculatePathOffset] Calculating offset for path: ${path.join('→')}`)

  // Sum offsets between consecutive frames
  for (let i = 0; i < path.length - 1; i++) {
    const fromFrame = path[i]
    const toFrame = path[i + 1]

    // The offset key is always smaller→larger (e.g., "5_10")
    const keyFrom = Math.min(fromFrame, toFrame)
    const keyTo = Math.max(fromFrame, toFrame)
    const key = `${keyFrom}_${keyTo}`

    // Try all_pairs first, then sequential
    const offset = offsetMap.all_pairs?.[key] || offsetMap.sequential?.[key]

    if (offset) {
      // If we're traversing from keyFrom to keyTo (matches offset direction), add offset
      // If we're traversing from keyTo to keyFrom (opposite direction), subtract offset
      const isForwardDirection = fromFrame === keyFrom && toFrame === keyTo
      const direction = isForwardDirection ? 1 : -1
      
      const segmentOffset = {
        x: offset.dx * direction,
        y: offset.dy * direction
      }
      
      totalX += segmentOffset.x
      totalY += segmentOffset.y
      
      // console.log(`  Segment ${fromFrame}→${toFrame}: key=${key}, offset=(${offset.dx.toFixed(1)}, ${offset.dy.toFixed(1)}), direction=${direction}, segment=(${segmentOffset.x.toFixed(1)}, ${segmentOffset.y.toFixed(1)}), total=(${totalX.toFixed(1)}, ${totalY.toFixed(1)})`)
    } else {
      // console.warn(`[calculatePathOffset] Missing offset for frames ${fromFrame}→${toFrame} (key: ${key})`)
    }
  }

  // console.log(`[calculatePathOffset] Final offset for path ${path[0]}→${path[path.length-1]}: (${totalX.toFixed(1)}, ${totalY.toFixed(1)})`)
  return { x: totalX, y: totalY }
}

/**
 * Get the displacement (dx, dy) from one frame to another, even if not directly connected.
 * Uses graph-based path finding to find a route and accumulates offsets along the path.
 *
 * @param fromFrame - Starting frame index
 * @param toFrame - Target frame index
 * @param connectivityGraph - Map of frame connections
 * @param offsetMap - Offset map for displacements
 * @returns Displacement {x, y} or null if no path exists
 */
export function getFrameDisplacement(
  fromFrame: number,
  toFrame: number,
  connectivityGraph: Map<number, Set<number>> | null,
  offsetMap: OffsetMap | null
): { x: number; y: number } | null {
  const framePath = findFramePath(fromFrame, toFrame, connectivityGraph, offsetMap)

  if (!framePath.valid) {
    return null
  }

  return framePath.totalOffset
}

/**
 * Cache for frame displacements to improve performance
 */
const displacementCache = new Map<string, { x: number; y: number } | null>()

/**
 * Get cached frame displacement with automatic cache management
 */
export function getCachedFrameDisplacement(
  fromFrame: number,
  toFrame: number,
  connectivityGraph: Map<number, Set<number>> | null,
  offsetMap: OffsetMap | null
): { x: number; y: number } | null {
  const key = `${fromFrame}_${toFrame}`

  if (displacementCache.has(key)) {
    return displacementCache.get(key)!
  }

  const displacement = getFrameDisplacement(fromFrame, toFrame, connectivityGraph, offsetMap)
  
  // Debug logging for context frames
  if (displacement && Math.abs(displacement.x) > 10) {
    // console.log(`[FrameDisplacement] Frame ${fromFrame} → ${toFrame}: dx=${displacement.x.toFixed(1)}, dy=${displacement.y.toFixed(1)}`)
  }
  
  displacementCache.set(key, displacement)

  // Limit cache size to prevent memory issues
  if (displacementCache.size > 10000) {
    const firstKey = displacementCache.keys().next().value
    if (firstKey) {
      displacementCache.delete(firstKey)
    }
  }

  return displacement
}

/**
 * Clear the displacement cache (useful when connectivity or offsets change)
 */
export function clearDisplacementCache(): void {
  displacementCache.clear()
}

/**
 * Build extended connectivity graph that includes ALL reachable frames (not just direct neighbors).
 * This uses path finding to determine if any path exists between frames.
 * 
 * Used for context frame visualization to show frames from all directions.
 * Should be calculated ONCE when offset map loads.
 * 
 * @param connectivityGraph - Direct connectivity graph (only adjacent frames)
 * @param offsetMap - Offset map for path finding
 * @param totalFrames - Total number of frames in the dataset
 * @returns Extended graph where each frame maps to ALL reachable frames (via any path)
 */
export function buildExtendedConnectivityGraph(
  connectivityGraph: Map<number, Set<number>> | null,
  offsetMap: OffsetMap | null,
  totalFrames: number
): Map<number, Set<number>> {
  const extendedGraph = new Map<number, Set<number>>()
  
  if (!connectivityGraph || !offsetMap) {
    console.warn('[buildExtendedConnectivityGraph] Missing connectivity graph or offset map')
    return extendedGraph
  }

  // console.log(`[buildExtendedConnectivityGraph] Building extended connectivity for ${totalFrames} frames...`)
  const startTime = performance.now()
  
  let pathsFound = 0
  let pathsNotFound = 0

  // For each frame, find all reachable frames
  for (let fromFrame = 0; fromFrame < totalFrames; fromFrame++) {
    const reachableFrames = new Set<number>()
    
    // Check path to every other frame
    for (let toFrame = 0; toFrame < totalFrames; toFrame++) {
      if (fromFrame === toFrame) {
        reachableFrames.add(toFrame) // Frame can reach itself
        continue
      }
      
      // Use BFS to check if path exists (don't calculate offset yet - just check reachability)
      const pathExists = checkPathExists(fromFrame, toFrame, connectivityGraph)
      
      if (pathExists) {
        reachableFrames.add(toFrame)
        pathsFound++
      } else {
        pathsNotFound++
      }
    }
    
    extendedGraph.set(fromFrame, reachableFrames)
  }

  const endTime = performance.now()
  const avgReachable = Array.from(extendedGraph.values()).reduce((sum, set) => sum + set.size, 0) / totalFrames
  
  // console.log(`[buildExtendedConnectivityGraph] ✅ Complete in ${(endTime - startTime).toFixed(0)}ms`)
  // console.log(`  Total frames: ${totalFrames}`)
  // console.log(`  Paths found: ${pathsFound}`)
  // console.log(`  Paths not found: ${pathsNotFound}`)
  // console.log(`  Average reachable per frame: ${avgReachable.toFixed(1)}`)
  
  return extendedGraph
}

/**
 * Fast check if a path exists between two frames (without calculating full path/offset).
 * Uses BFS but stops as soon as target is found.
 * 
 * @param startFrame - Starting frame
 * @param endFrame - Target frame
 * @param connectivityGraph - Direct connectivity graph
 * @returns true if path exists, false otherwise
 */
function checkPathExists(
  startFrame: number,
  endFrame: number,
  connectivityGraph: Map<number, Set<number>>
): boolean {
  if (startFrame === endFrame) return true
  
  const visited = new Set<number>()
  const queue: number[] = [startFrame]
  visited.add(startFrame)

  while (queue.length > 0) {
    const frame = queue.shift()!
    
    if (frame === endFrame) {
      return true // Found path
    }

    const neighbors = connectivityGraph.get(frame)
    if (neighbors) {
      for (const neighbor of neighbors) {
        if (!visited.has(neighbor)) {
          visited.add(neighbor)
          queue.push(neighbor)
        }
      }
    }
  }

  return false // No path found
}

// Clear cache on module load since we fixed the calculation logic
clearDisplacementCache()